import type { DataProvider, GetListParams, GetOneParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchGiftcards } from './fetchGiftcards';
import type { FetchGiftcardsFilters, GiftcardRecord } from './types';

// The list filter values react-admin threads through (it types `filter` as
// `any`); narrowing here keeps the mapping below type-checked.
interface GiftcardFilter {
  state?: string;
  source?: string;
  reason?: string;
  giftcardNumber?: string;
  orderNumber?: string;
  tradeinNumber?: string;
  amount?: string | number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

// Coerce a free-text numeric filter, dropping blanks and non-numeric input.
// Without the NaN guard a typo reaches the API as `null`, which the server
// discards — so the list comes back unfiltered and reads as a match.
const toNumber = (value: string | number | undefined): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

// Amount filter is entered in dollars; the API expects cents.
//
// Zero is dropped rather than sent. The server's filtersToWhere (reware-api
// lib/services/util.js) keeps a filter only `if (filters[key])`, so `amount: 0` is discarded and
// the query runs unfiltered — the caller would get every gift card back while the filter chip
// claimed a $0 search was applied. Sending a value we know is ignored is the same failure the
// NaN guard above exists to prevent. The input also rejects 0, so this is the second line.
const toAmountCents = (value: string | number | undefined): number | undefined => {
  const dollars = toNumber(value);
  return dollars ? Math.round(dollars * 100) : undefined;
};

const toApiFilters = (params: GetListParams): FetchGiftcardsFilters => {
  const { page = 1, perPage = 20 } = params.pagination ?? {};
  const filter = (params.filter ?? {}) as GiftcardFilter;

  return {
    partnerId: requirePartner().id,
    page,
    pageSize: perPage,
    state: filter.state || undefined,
    source: filter.source || undefined,
    reason: filter.reason || undefined,
    // No giftcardId here. No input supplies one, and `getOne` builds its own filters, so the
    // only way to set it was a hand-written URL — where a non-numeric value was silently
    // dropped and the caller got an unfiltered page that read as a match. Omitting it means
    // such a URL is ignored as an unknown key rather than half-honoured.
    giftcardNumber: filter.giftcardNumber || undefined,
    orderNumber: filter.orderNumber || undefined,
    tradeinNumber: filter.tradeinNumber || undefined,
    amount: toAmountCents(filter.amount),
    firstName: filter.firstName || undefined,
    lastName: filter.lastName || undefined,
    email: filter.email || undefined,
    phone: filter.phone || undefined,
  };
};

/**
 * Read-only provider: only getList and getOne are supported. The remaining
 * DataProvider methods are stubbed to throw so an unsupported call fails with a
 * clear message rather than an opaque "undefined is not a function" (same stub
 * pattern as src/inventoryItems/dataProvider.ts).
 */
interface GiftcardsDataProvider {
  getList: (
    resource: string,
    params: GetListParams
  ) => Promise<{ data: GiftcardRecord[], total: number }>;
  getOne: (
    resource: string,
    params: GetOneParams
  ) => Promise<{ data: GiftcardRecord }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

const giftcardsDataProviderImpl: GiftcardsDataProvider = {
  getList: async (_resource, params) => {
    const result = await fetchGiftcards(toApiFilters(params));
    return { data: result.rows, total: result.count };
  },

  getOne: async (_resource, params) => {
    // Validate before querying. A NaN id serializes as null, which the server drops, leaving an
    // unfiltered query — and with pageSize 1 the partner's newest card would come back, pass the
    // not-found check below, and be returned as the record that was asked for.
    const giftcardId = Number(params.id);
    if (!Number.isInteger(giftcardId)) {
      throw new Error(`Gift card ${params.id} not found`);
    }

    const result = await fetchGiftcards({
      partnerId: requirePartner().id,
      page: 1,
      pageSize: 1,
      giftcardId,
    });
    if (!result.rows.length) {
      throw new Error(`Gift card ${params.id} not found`);
    }
    return { data: result.rows[0] };
  },

  getMany: async () => { throw new Error('getMany not implemented for giftcards resource'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for giftcards resource'); },
  create: async () => { throw new Error('create not implemented for giftcards resource'); },
  update: async () => { throw new Error('update not implemented for giftcards resource'); },
  updateMany: async () => { throw new Error('updateMany not implemented for giftcards resource'); },
  delete: async () => { throw new Error('delete not implemented for giftcards resource'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for giftcards resource'); },
};

// React-admin's DataProvider methods are generic, which prevents a type-safe
// single-resource provider; we define proper types above and cast here.
export const giftcardsDataProvider =
  giftcardsDataProviderImpl as unknown as DataProvider;
